import { apiRequest } from "@utils/apiRequest";
import { getListIds } from "@utils/functions";
import { store as pageSizeStore } from '@molecules/paginationControls/pageSizeStore';
import { useEffect, useState } from "react";
import { TBatchModel } from "types/batchFields";
import { TMetadata } from "types/metadata";
import { TInfodata } from "types/infodata";
import { urlCategoryId } from "@utils/urlCategoryId";

type TMetaAdditional = TMetadata & TInfodata

interface IUsePageList {
    batchModels: TBatchModel[]
    onDeleteSelected?: () => void
    onRestoreSelected?: () => void
    onReinitList?: () => void
}

const DEF_IS_LOADING = false;
const DEF_PAGE_NUMBER = 1;
const DEF_SELECTED_ITEMS: Array<{ id: number; name: string; profileId?: number }> = [];

function initSelectedItems(models: TBatchModel[]) {
    const res = models.reduce((sum, model) => ({
        ...sum,
        [model]: DEF_SELECTED_ITEMS,
        }), {} as { [K in TBatchModel]?: { id: number; name: string; profileId?: number; parentId: number | null }[] }
    )

    return res
}

export const usePageList = ({
    batchModels = [],
    onDeleteSelected = async () => null,
    onRestoreSelected = async () => null,
    onReinitList = async () => null,
}: IUsePageList) => {
    const [forceReloadState, setForceReloadState] = useState(0);
    const [isLoading, setIsLoading] = useState(DEF_IS_LOADING);
    const [pageNumber, setPageNumber] = useState(DEF_PAGE_NUMBER);
    const [pageSize, setPageSize] = useState(pageSizeStore.pS);
    const [categoryId, setCategoryId] = useState(urlCategoryId());
    const [queryMeta, setQueryMeta] = useState<TMetaAdditional>(null);
    const [selectedItems, setSelectedItems] = useState(initSelectedItems(batchModels));

    const updateSelectedItems = (model: TBatchModel, newSelectedItems) => {
        return setSelectedItems({
            ...selectedItems,
            [model]: [...newSelectedItems,]
        })
    }

    function resetSelectedItems() {
        setSelectedItems(initSelectedItems(batchModels))
    }

    async function reinitList() {
        onReinitList()
        setSelectedItems(initSelectedItems(batchModels))
        // setPageNumber(DEF_PAGE_NUMBER)
        setForceReloadState((x) => x + 1)
    }
    
    async function actionSelected(type: 'batchDelete' | 'batchRestore' | 'batchMove') {
        setIsLoading(true)

        const selectedIds = batchModels.reduce((sum, model) => {
            const items = selectedItems[model] || [];
            const ids = model === 'customer_profiles'
                ? items.map((item: {id: number; profileId?: number}) => item.profileId ?? item.id)
                : getListIds(items);
            return {
                ...sum,
                [model]: ids,
            };
        }, {} as { [K in TBatchModel]?: number[] })

        if (type === 'batchDelete') {
            onDeleteSelected()
        }
        if (type === 'batchRestore') {
            onRestoreSelected()
        }

        await apiRequest(
            ...batchModels.filter(model => selectedIds[model].length).map((model) => {
                return (async (api, crmId) => {
                    return await api[type]({ids: selectedIds[model], crm_id: crmId, model: model})
                })
            })
        )

        await reinitList()

        setIsLoading(false)
    }

    useEffect(() => {
        const newPageSize = pageSizeStore.pS;
        if (pageSize !== newPageSize) {
            setPageNumber(Math.ceil((pageNumber * pageSize + 1) / newPageSize))
            setPageSize(newPageSize)
        }
    }, [pageSizeStore.pS])

    function deleteSelectedItems() {
        actionSelected('batchDelete')
    }

    function restoreSelectedItems() {
        actionSelected('batchRestore')
    }

    function pageMore() {
        setPageNumber(pageNumber + 1)
    }

    function pageFrom(newPage: number) {
        setPageNumber(newPage)
    }

    return {
        forceReloadState,

        isLoading,
        setIsLoading,
        
        pageNumber,
        setPageNumber,

        pageSize,
        setPageSize,

        queryMeta,
        setQueryMeta,

        selectedItems,
        updateSelectedItems,
        deleteSelectedItems,

        restoreSelectedItems,
        resetSelectedItems,

        categoryId,
        setCategoryId,

        pageMore,
        pageFrom,
    }
}